import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { AzureOnboardingStatusResponse } from '../models/AzureOnboardingStatusResponse';
import { OnboardAzureSourceAccountInteractive200Response } from '../models/OnboardAzureSourceAccountInteractive200Response';
import { OnboardAzureSourceAccountInteractive400Response } from '../models/OnboardAzureSourceAccountInteractive400Response';
import { OnboardAzureSourceAccountInteractive401Response } from '../models/OnboardAzureSourceAccountInteractive401Response';
import { OnboardAzureSourceAccountInteractive500Response } from '../models/OnboardAzureSourceAccountInteractive500Response';
import { OnboardAzureSourceAccountInteractiveRequest } from '../models/OnboardAzureSourceAccountInteractiveRequest';

/**
 * no description
 */
export class AzureAccountManagementApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * Azure interactive onboarding status
   * @param projectId The project ID
   * @param requestId The request ID for tracking onboarding status
   */
  public async getAzureInteractiveOnboardingStatus(projectId: string, requestId: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AzureAccountManagementApi", "getAzureInteractiveOnboardingStatus", "projectId");
    }


    // verify required parameter 'requestId' is not null or undefined
    if (requestId === null || requestId === undefined) {
      throw new RequiredError("AzureAccountManagementApi", "getAzureInteractiveOnboardingStatus", "requestId");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/azure/onboarding/interactive/status/{requestId}'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)))
      .replace('{' + 'requestId' + '}', encodeURIComponent(String(requestId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

  /**
   * Interactive Source Account Onboarding
   * @param projectId The project ID
   * @param onboardAzureSourceAccountInteractiveRequest 
   * @param requestId Optional request ID for tracking (will be generated if not provided)
   */
  public async onboardAzureSourceAccountInteractive(projectId: string, onboardAzureSourceAccountInteractiveRequest: OnboardAzureSourceAccountInteractiveRequest, requestId?: string, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AzureAccountManagementApi", "onboardAzureSourceAccountInteractive", "projectId");
    }


    // verify required parameter 'onboardAzureSourceAccountInteractiveRequest' is not null or undefined
    if (onboardAzureSourceAccountInteractiveRequest === null || onboardAzureSourceAccountInteractiveRequest === undefined) {
      throw new RequiredError("AzureAccountManagementApi", "onboardAzureSourceAccountInteractive", "onboardAzureSourceAccountInteractiveRequest");
    }



    // Path Params
    const localVarPath = '/projects/{projectId}/azure/account/interactive-onboarding/source'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (requestId !== undefined) {
      requestContext.setQueryParam("requestId", ObjectSerializer.serialize(requestId, "string", "uuid"));
    }


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(onboardAzureSourceAccountInteractiveRequest, "OnboardAzureSourceAccountInteractiveRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class AzureAccountManagementApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to getAzureInteractiveOnboardingStatus
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async getAzureInteractiveOnboardingStatusWithHttpInfo(response: ResponseContext): Promise<HttpInfo<AzureOnboardingStatusResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: AzureOnboardingStatusResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "AzureOnboardingStatusResponse", ""
      ) as AzureOnboardingStatusResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Bad request", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Request not found", undefined, response.headers, response);
    }
    if (isCodeInRange("500", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Internal server error", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: AzureOnboardingStatusResponse = await response.body.json() as AzureOnboardingStatusResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to onboardAzureSourceAccountInteractive
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async onboardAzureSourceAccountInteractiveWithHttpInfo(response: ResponseContext): Promise<HttpInfo<OnboardAzureSourceAccountInteractive200Response >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: OnboardAzureSourceAccountInteractive200Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "OnboardAzureSourceAccountInteractive200Response", ""
      ) as OnboardAzureSourceAccountInteractive200Response;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      const body: OnboardAzureSourceAccountInteractive400Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "OnboardAzureSourceAccountInteractive400Response", ""
      ) as OnboardAzureSourceAccountInteractive400Response;
      throw new ApiException<OnboardAzureSourceAccountInteractive400Response>(response.httpStatusCode, "Bad request - invalid parameters", body, response.headers, response);
    }
    if (isCodeInRange("401", response.httpStatusCode)) {
      const body: OnboardAzureSourceAccountInteractive401Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "OnboardAzureSourceAccountInteractive401Response", ""
      ) as OnboardAzureSourceAccountInteractive401Response;
      throw new ApiException<OnboardAzureSourceAccountInteractive401Response>(response.httpStatusCode, "Unauthorized - invalid or missing authentication", body, response.headers, response);
    }
    if (isCodeInRange("500", response.httpStatusCode)) {
      const body: OnboardAzureSourceAccountInteractive500Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "OnboardAzureSourceAccountInteractive500Response", ""
      ) as OnboardAzureSourceAccountInteractive500Response;
      throw new ApiException<OnboardAzureSourceAccountInteractive500Response>(response.httpStatusCode, "Internal server error", body, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: OnboardAzureSourceAccountInteractive200Response = await response.body.json() as OnboardAzureSourceAccountInteractive200Response;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
